import {useEffect, useRef, useState} from 'react';
import {Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import {useTranslation} from 'react-i18next';
import type {DocsManifestEntry} from '@/types/docsManifest';
import {buildDocAssetUrl, buildViewerUrl, copyText, downloadDocFile, openDocInNewTab, printFrameWindow, printPdfFile} from './docsMenuUtils';
import {useIsMobile} from '@/hooks/useIsMobile';
import './DocViewerDialog.css';

const DOC_CONTENT_MAX_WIDTH = 900;
const DOC_DIALOG_CHROME_WIDTH = 140;
const DOC_DIALOG_WIDTH_MULTIPLIER = 1.5;
const DOC_VIEWER_SCREEN_PADDING_INLINE = 'clamp(8px, 1vw, 16px)';
const DOC_VIEWER_SCREEN_PADDING_BLOCK = '24px';
const DOC_VIEWER_SCREEN_MAX_WIDTH = 'min(1120px, calc(100% - 48px))';

interface DocViewerDialogProps {
    selectedDoc: DocsManifestEntry | null;
    onClose: () => void;
}

const DocViewerDialog = ({selectedDoc, onClose}: DocViewerDialogProps) => {
    const {t} = useTranslation();
    const [iframeReady, setIframeReady] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        setIframeReady(false);
    }, [selectedDoc?.file]);

    if (!selectedDoc) return null;

    const handleCopyUrl = async () => {
        await copyText(buildViewerUrl(selectedDoc.file));
    };

    const handleHtmlPrint = () => {
        try {
            const frameWindow = iframeRef.current?.contentWindow;
            if (!printFrameWindow(frameWindow)) {
                openDocInNewTab(selectedDoc.file);
            }
        } catch {
            openDocInNewTab(selectedDoc.file);
        }
    };

    const handlePrint = () => {
        if (selectedDoc.pdfFile) {
            printPdfFile(selectedDoc.pdfFile, handleHtmlPrint);
            return;
        }
        handleHtmlPrint();
    };

    const handleDownloadXlsx = () => {
        if (selectedDoc.xlsxFile) {
            downloadDocFile(selectedDoc.xlsxFile);
        }
    };

    const handleSaveAsPdf = () => {
        if (selectedDoc.pdfFile) {
            downloadDocFile(selectedDoc.pdfFile);
            return;
        }
        handlePrint();
    };

    const handleIframeLoad = () => {
        try {
            const iframeDocument = iframeRef.current?.contentDocument;
            if (iframeDocument?.head && iframeDocument.body) {
                let viewerStyle = iframeDocument.getElementById('doc-viewer-screen-style') as HTMLStyleElement | null;
                if (!viewerStyle) {
                    viewerStyle = iframeDocument.createElement('style');
                    viewerStyle.id = 'doc-viewer-screen-style';
                    iframeDocument.head.appendChild(viewerStyle);
                }

                viewerStyle.textContent = `
                    @media screen {
                        html {
                            background: #f3f5f8;
                        }
                        body {
                            box-sizing: border-box;
                            max-width: ${DOC_VIEWER_SCREEN_MAX_WIDTH};
                            margin: ${DOC_VIEWER_SCREEN_PADDING_BLOCK} auto;
                            padding: ${DOC_VIEWER_SCREEN_PADDING_BLOCK} ${DOC_VIEWER_SCREEN_PADDING_INLINE};
                            background: #ffffff;
                            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
                        }
                    }
                    @media screen and (max-width: 768px) {
                        html {
                            background: #ffffff;
                        }
                        body {
                            max-width: 100%;
                            margin: 0;
                            padding: 16px 16px 8px;
                            box-shadow: none;
                            font-size: 15px;
                            line-height: 1.65;
                            -webkit-text-size-adjust: 100%;
                        }
                        img, table, pre {
                            max-width: 100%;
                            overflow-x: auto;
                        }
                        table {
                            display: block;
                            overflow-x: auto;
                            -webkit-overflow-scrolling: touch;
                        }
                        pre, code {
                            font-size: 13px;
                            white-space: pre-wrap;
                            word-break: break-word;
                        }
                    }
                `;

                // External links must open in a new tab instead of navigating
                // the viewer iframe. Any anchor resolving to a different origin
                // (e.g. https://safety-hub.web.cern.ch) opens with _blank;
                // in-page anchors (#...) and relative asset links stay inline.
                iframeDocument.querySelectorAll('a[href]').forEach(node => {
                    const anchor = node as HTMLAnchorElement;
                    const href = anchor.getAttribute('href');
                    if (!href || href.startsWith('#')) {
                        return;
                    }
                    if (anchor.origin && anchor.origin !== iframeDocument.location.origin) {
                        anchor.target = '_blank';
                        anchor.rel = 'noopener noreferrer';
                    }
                });
            }
        } catch {
            // Ignore cross-document access issues and fall back to the raw document.
        }

        setIframeReady(true);
    };

    /* ── Mobile: full-screen reader ─────────────────────────────── */
    if (isMobile) {
        return (
            <Dialog
                open
                onClose={onClose}
                fullScreen
                className="doc-viewer-dialog doc-viewer-dialog--mobile"
                slotProps={{
                    paper: {
                        sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        },
                    },
                }}
            >
                <div className="doc-viewer-mobile-header">
                    <IconButton
                        onClick={onClose}
                        className="doc-viewer-back-btn"
                        aria-label={t('common.close')}
                        edge="start"
                        size="small"
                    >
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                    <Typography component="h2" className="doc-viewer-mobile-title">
                        {selectedDoc.title}
                    </Typography>
                </div>

                <div className="doc-viewer-mobile-content">
                    <iframe
                        ref={iframeRef}
                        src={buildDocAssetUrl(selectedDoc.file)}
                        title={selectedDoc.title}
                        onLoad={handleIframeLoad}
                        className="doc-viewer-iframe"
                        style={{visibility: iframeReady ? 'visible' : 'hidden'}}
                    />
                </div>

                <div className="doc-viewer-mobile-actions">
                    <button className="doc-viewer-action-btn" onClick={handlePrint} aria-label={t('common.print')}>
                        <PrintIcon/>
                        <span>{t('common.print')}</span>
                    </button>
                    <button
                        className="doc-viewer-action-btn doc-viewer-action-btn--save"
                        onClick={handleSaveAsPdf}
                        aria-label={t('common.saveAsPdf')}
                    >
                        <PictureAsPdfIcon/>
                        <span>{t('common.saveAsPdf')}</span>
                    </button>
                    {selectedDoc.xlsxFile && (
                        <button className="doc-viewer-action-btn" onClick={handleDownloadXlsx} aria-label={t('common.downloadXlsx', {defaultValue: 'Download Excel Test Plan'})}>
                            <TableChartIcon/>
                            <span>{t('common.downloadXlsx', {defaultValue: 'Download Excel Test Plan'})}</span>
                        </button>
                    )}
                    <button className="doc-viewer-action-btn" onClick={handleCopyUrl} aria-label={t('common.copyUrl')}>
                        <ContentCopyIcon/>
                        <span>{t('common.copyUrl')}</span>
                    </button>
                </div>
            </Dialog>
        );
    }

    /* ── Desktop: original layout ───────────────────────────────── */
    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth={false}
            slotProps={{
                paper: {
                    sx: {
                        width: `min(calc((${DOC_CONTENT_MAX_WIDTH}px + ${DOC_DIALOG_CHROME_WIDTH}px) * ${DOC_DIALOG_WIDTH_MULTIPLIER}), calc(100vw - 32px))`,
                        height: '90vh',
                        maxWidth: 'none',
                        display: 'flex',
                        overflow: 'hidden',
                    },
                },
            }}
        >
            <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                <Typography component="span" variant="h6" sx={{flex: 1}}>
                    {selectedDoc.title}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handlePrint}
                        startIcon={<PrintIcon/>}
                    >
                        {t('common.print')}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={handleSaveAsPdf}
                        aria-label={t('common.saveAsPdf')}
                        startIcon={<PictureAsPdfIcon/>}
                    >
                        {t('common.saveAsPdf')}
                    </Button>
                    {selectedDoc.xlsxFile && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleDownloadXlsx}
                            startIcon={<TableChartIcon/>}
                        >
                            {t('common.downloadXlsx', {defaultValue: 'Download Excel Test Plan'})}
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleCopyUrl}
                        startIcon={<ContentCopyIcon/>}
                    >
                        {t('common.copyUrl')}
                    </Button>
                    <IconButton onClick={onClose} aria-label={t('common.close')}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{p: 0, overflow: 'hidden', display: 'flex', flex: 1}}>
                <iframe
                    ref={iframeRef}
                    src={buildDocAssetUrl(selectedDoc.file)}
                    title={selectedDoc.title}
                    onLoad={handleIframeLoad}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        display: 'block',
                        flex: 1,
                        visibility: iframeReady ? 'visible' : 'hidden',
                    }}
                />
            </DialogContent>
        </Dialog>
    );
};

export default DocViewerDialog;
